<script setup lang="ts">
/**
 * Recherche d'un intervenant pour une action d'accompagnement :
 *   - kind="INTERNE" → recherche d'un utilisateur (/material/users)
 *   - kind="CONTACT" → recherche d'un contact CRM (/contacts/list)
 * Émet `select` avec les coordonnées normalisées (+ uuid de l'enregistrement).
 * Combobox custom DaisyUI (pas de lib externe).
 */
import { ref, watch } from 'vue'
import { MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/vue/24/outline'
import { accompagnementApi } from '@/v2/accompagnement/api/accompagnementApi'
import { listContacts } from '@/api/contactsApi'
import type { AccompActorKind } from '@/v2/accompagnement/api/accompagnementTypes'

interface ActorPick {
  uuid: string
  firstName: string
  lastName: string
  email: string
  phone: string
}

// Utilisé uniquement pour INTERNE (users) et CONTACT (contacts CRM).
const props = defineProps<{ kind: AccompActorKind; id?: string }>()
const emit = defineEmits<{ select: [ActorPick] }>()

interface Row extends ActorPick {
  name: string
}

const open = ref(false)
const query = ref('')
const results = ref<Row[]>([])
const loading = ref(false)
const activeIndex = ref(-1)
const root = ref<HTMLElement | null>(null)

let debounce: ReturnType<typeof setTimeout> | null = null
let abort: AbortController | null = null

function displayName(first: string, last: string, email: string): string {
  return `${first} ${last}`.trim() || email || '—'
}

async function search() {
  const term = query.value.trim()
  abort?.abort()
  if (!term) {
    results.value = []
    activeIndex.value = -1
    loading.value = false
    return
  }
  abort = new AbortController()
  loading.value = true
  try {
    if (props.kind === 'INTERNE') {
      const users = await accompagnementApi.searchUsers(term, abort.signal)
      results.value = users.map((u) => {
        const firstName = u.firstName ?? ''
        const lastName = u.lastName ?? ''
        const email = u.email ?? ''
        return { uuid: u.uuid, firstName, lastName, email, phone: u.phone ?? '', name: displayName(firstName, lastName, email) }
      })
    } else {
      const res = await listContacts({
        q: term,
        limit: 8,
        offset: 0,
        sort: 'lastname',
        order: 'asc',
        signal: abort.signal,
      })
      results.value = res.data.map((c) => {
        const firstName = c.firstname ?? ''
        const lastName = c.lastname ?? ''
        const email = c.email ?? ''
        return {
          uuid: c.uuid,
          firstName,
          lastName,
          email,
          phone: c.phone ?? c.mobile ?? '',
          name: displayName(firstName, lastName, email),
        }
      })
    }
    activeIndex.value = results.value.length ? 0 : -1
    loading.value = false
  } catch (e: unknown) {
    if (e instanceof Error && e.name === 'AbortError') return
    results.value = []
    loading.value = false
  }
}

function onInput(e: Event) {
  query.value = (e.target as HTMLInputElement).value
  open.value = true
  if (debounce) clearTimeout(debounce)
  loading.value = !!query.value.trim()
  debounce = setTimeout(() => {
    debounce = null
    void search()
  }, 350)
}

function pick(r: Row) {
  emit('select', { uuid: r.uuid, firstName: r.firstName, lastName: r.lastName, email: r.email, phone: r.phone })
  open.value = false
  query.value = ''
  results.value = []
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    open.value = false
    return
  }
  if (!results.value.length) return
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    activeIndex.value = (activeIndex.value + 1) % results.value.length
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    activeIndex.value = (activeIndex.value - 1 + results.value.length) % results.value.length
  } else if (e.key === 'Enter') {
    e.preventDefault()
    const r = results.value[activeIndex.value]
    if (r) pick(r)
  }
}

function onClickOutside(e: MouseEvent) {
  if (root.value && !root.value.contains(e.target as Node)) open.value = false
}
watch(open, (v) => {
  if (v) document.addEventListener('mousedown', onClickOutside)
  else document.removeEventListener('mousedown', onClickOutside)
})
</script>

<template>
  <div ref="root" class="relative">
    <div class="flex items-center gap-2 rounded-lg border border-base-300 bg-base-100 px-3 py-1.5 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
      <MagnifyingGlassIcon
        class="h-4 w-4 shrink-0"
        :class="loading ? 'text-primary' : 'text-base-content/40'"
        aria-hidden="true"
      />
      <input
        :id="id"
        type="text"
        class="flex-1 bg-transparent text-base text-base-content outline-none placeholder:text-base-content/40"
        :placeholder="kind === 'INTERNE' ? 'Rechercher un collaborateur (nom, prénom, email)…' : 'Rechercher un contact (nom, prénom, email)…'"
        :value="query"
        role="combobox"
        aria-expanded="true"
        aria-autocomplete="list"
        @input="onInput"
        @focus="open = true"
        @keydown="onKeydown"
      />
      <ArrowPathIcon v-if="loading" class="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
    </div>

    <ul
      v-if="open && query.trim()"
      role="listbox"
      class="absolute z-30 mt-1 max-h-60 w-full overflow-y-auto rounded-box border border-base-300 bg-base-100 p-1 shadow-lg"
    >
      <li v-if="loading && !results.length" class="px-3 py-2 text-sm text-base-content/70">Recherche…</li>
      <li v-else-if="!results.length" class="px-3 py-2 text-sm text-base-content/70">Aucun résultat.</li>
      <li v-for="(r, i) in results" v-else :key="r.uuid">
        <button
          type="button"
          role="option"
          :aria-selected="i === activeIndex"
          class="flex w-full flex-col items-start rounded-md px-3 py-2 text-left"
          :class="i === activeIndex ? 'bg-base-200' : 'hover:bg-base-200/60'"
          @mousemove="activeIndex = i"
          @click="pick(r)"
        >
          <span class="font-medium text-base-content">{{ r.name }}</span>
          <span v-if="r.email" class="text-sm text-base-content/70">{{ r.email }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>
